import { useEffect, useState } from 'react';
import { useRegisterSW } from 'virtual:pwa-register/react';
import { RefreshCw } from 'lucide-react';
import { useTranslation } from '../context/LanguageContext';
import { Button } from './ui/button';

export function AppUpdateNotice() {
  const { lang } = useTranslation();
  const [updateReady, setUpdateReady] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [registration, setRegistration] = useState<ServiceWorkerRegistration>();

  useRegisterSW({
    // An activated worker updates the cache, not the JavaScript already running
    // in this tab. Ask the user to reload so unsaved forms remain intact.
    onNeedReload: () => setUpdateReady(true),
    onRegisteredSW: (_url, worker) => setRegistration(worker),
  });

  useEffect(() => {
    if (!registration) return;
    let checking = false;
    const checkForUpdate = async () => {
      if (checking || !navigator.onLine || document.visibilityState !== 'visible') return;
      checking = true;
      try {
        await registration.update();
      } catch {
        // A temporary connection failure must not interrupt work.
      } finally {
        checking = false;
      }
    };
    const timer = window.setInterval(checkForUpdate, 60_000);
    window.addEventListener('focus', checkForUpdate);
    window.addEventListener('online', checkForUpdate);
    document.addEventListener('visibilitychange', checkForUpdate);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener('focus', checkForUpdate);
      window.removeEventListener('online', checkForUpdate);
      document.removeEventListener('visibilitychange', checkForUpdate);
    };
  }, [registration]);

  if (!updateReady || dismissed) return null;

  return (
    <div
      role="status"
      data-testid="app-update-notice"
      className="fixed bottom-20 left-3 right-3 z-[60] flex flex-col gap-3 rounded-xl border bg-background p-4 text-foreground shadow-lg sm:bottom-6 sm:left-auto sm:right-6 sm:max-w-sm"
    >
      <p className="text-sm">
        {lang === 'UZ'
          ? 'Yangilanish tayyor. O‘zgarishlarni saqlang va sahifani yangilang.'
          : 'Доступно обновление. Сохраните изменения и обновите страницу.'}
      </p>
      <div className="flex items-center justify-end gap-2">
        <Button variant="outline" onClick={() => setDismissed(true)}>
          {lang === 'UZ' ? 'Keyinroq' : 'Позже'}
        </Button>
        <Button onClick={() => window.location.reload()}>
          <RefreshCw className="mr-2 h-4 w-4" aria-hidden="true" />
          {lang === 'UZ' ? 'Sahifani yangilash' : 'Обновить страницу'}
        </Button>
      </div>
    </div>
  );
}
